import { type Lesson } from "@core/grades/types";
import { formatDuration, formatNumber, money, URLS } from "@core/shared/utils";
import { cn } from "@design-system/utils";
import { settingsAtom } from "@framework/shared/atoms";
import { trans } from "@mongez/localization";
import { Clock3, Eye, GraduationCap, UserRound } from "lucide-react";
import { BaseLink } from "../../design-system/components/link";
import { LessonLikeButton } from "./LessonLikeButton";

type LessonCardProps = {
  lesson: Lesson;
  className?: string;
};

export function LessonCard({ lesson, className }: LessonCardProps) {
  const {
    name,
    image = settingsAtom.get("general")?.logo,
    instructor,
    views,
    duration,
    isFree,
    cost = 0,
    subject,
  } = lesson;

  return (
    <article
      className={cn(
        "group flex h-full flex-col overflow-hidden rounded-[1.5rem] border border-slate-200/80 bg-white shadow-sm transition duration-300 hover:-translate-y-1 hover:border-primary/30 hover:shadow-xl dark:border-slate-800 dark:bg-slate-900",
        className,
      )}
    >
      <BaseLink to={URLS.lessons.view(lesson)}>
        <div className="relative aspect-video w-full overflow-hidden bg-slate-100 dark:bg-slate-800">
          {image?.url ? (
            <img
              src={image.url}
              alt={name}
              className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
              loading="lazy"
            />
          ) : (
            <div className="grid h-full place-items-center bg-gradient-to-br from-primary/20 via-sky-100 to-violet-100 dark:from-primary/20 dark:via-slate-800 dark:to-violet-950/40">
              <GraduationCap className="size-14 text-primary/70" />
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950/55 via-transparent to-transparent" />
          {duration && (
            <span
              className="absolute bottom-3 end-3 inline-flex items-center gap-1 rounded-full bg-slate-950/80 px-2.5 py-1 text-[11px] font-bold text-white backdrop-blur"
              dir="ltr"
            >
              <Clock3 className="size-3" />
              {formatDuration(duration)}
            </span>
          )}
        </div>
      </BaseLink>
      <div className="flex flex-1 flex-col p-4">
        <div className="mb-3 flex items-center justify-between gap-2">
          <span className="max-w-[60%] truncate rounded-full bg-primary/10 px-2.5 py-1 text-[11px] font-black text-primary">
            {subject?.name}
          </span>
          <span className="shrink-0 text-[11px] font-bold text-slate-500 dark:text-slate-400">
            {isFree ? trans("free") : money(cost)}
          </span>
        </div>

        <div className="min-w-0 flex-1">
          <BaseLink to={URLS.lessons.view(lesson)}>
            <h3 className="line-clamp-2 min-h-12 text-base font-black leading-6 text-slate-950 transition group-hover:text-primary dark:text-white">
              {name}
            </h3>
          </BaseLink>

          <div className="mt-3 flex items-center gap-2 border-b border-slate-100 pb-3 dark:border-slate-800">
            {instructor?.image?.url ? (
              <img
                src={instructor.image.url}
                alt={instructor.name}
                className="size-8 rounded-full border-2 border-white object-cover shadow-sm dark:border-slate-800"
                loading="lazy"
              />
            ) : (
              <span className="grid size-8 place-items-center rounded-full bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-300">
                <UserRound className="size-4" />
              </span>
            )}
            <span className="min-w-0 truncate text-xs font-bold text-slate-600 dark:text-slate-300">
              {instructor?.name}
            </span>
          </div>

          <div className="mt-3 flex items-center justify-between gap-3 text-xs text-slate-500 dark:text-slate-400">
            <span className="inline-flex items-center gap-1" dir="ltr">
              <Eye className="size-3.5" />
              {formatNumber(views || 0)}
            </span>
            <LessonLikeButton lesson={lesson} compact />
          </div>
        </div>
      </div>
    </article>
  );
}
